/**
 * ExploreArchive — Subjects / Regions discovery tabs (replaces the
 * Intelligence Divisions presentation while reusing its taxonomy + art).
 * Cards expand inline to reveal real dossiers — no dead-end links.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Globe2, Layers } from "lucide-react";
import { SectionHeader, MiniRow } from "./homeShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const subjectArt = (slug) => `${API}/media/thumb/division-${slug}.png`;

const SubjectCard = ({ s, open, onToggle }) => (
  <button
    onClick={onToggle}
    data-testid={`explore-subject-${s.slug}`}
    aria-expanded={open}
    className="relative overflow-hidden border p-4 min-h-[110px] flex flex-col justify-end text-left group transition-all duration-300"
    style={{ borderColor: open ? (s.accent || "#D92D20") + "88" : "#2D2A26", background: "#0B0A0D" }}
  >
    <div
      aria-hidden="true"
      className="absolute inset-0 bg-cover bg-center opacity-35 group-hover:opacity-55 transition-opacity duration-500"
      style={{ backgroundImage: `url(${subjectArt(s.slug)})`, filter: "saturate(0.7)" }}
    />
    <div aria-hidden="true" className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,10,13,0.25) 0%, rgba(11,10,13,0.92) 100%)" }} />
    <div className="absolute top-0 left-0 h-[2px] w-10 group-hover:w-full transition-all duration-500" style={{ background: s.accent || "#D92D20" }} />
    <div className="relative">
      <div className="font-mono text-[8px] uppercase tracking-[0.35em] text-[#8A8377]">{s.count} dossiers</div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <h3 className="font-display text-[15px] md:text-[16px] leading-tight text-[#E4DFD3]">{s.label}</h3>
        <ChevronDown size={14} className={`shrink-0 text-[#8A8377] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </div>
    </div>
  </button>
);

const RegionRow = ({ r, open, onToggle }) => (
  <button
    onClick={onToggle}
    data-testid={`explore-region-${r.slug}`}
    aria-expanded={open}
    className="w-full flex items-center gap-3 px-4 py-4 border-b last:border-b-0 text-left group hover:bg-[#131015] transition-colors"
    style={{ borderColor: "#2D2A26" }}
  >
    <Globe2 size={14} className={open ? "text-[#D92D20]" : "text-[#8A8377]"} />
    <span className="flex-1 font-heading text-[15px] text-[#E4DFD3] group-hover:text-[#D92D20] transition-colors">
      {r.label}
    </span>
    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#8A8377]">{r.count} files</span>
    <ChevronDown size={14} className={`text-[#8A8377] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
  </button>
);

const Expansion = ({ items, testid }) => (
  <div
    data-testid={testid}
    className="border px-4 py-2 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
    style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
  >
    {items.map((d, i) => <MiniRow key={d.slug} dossier={d} index={i} testid={`${testid}-row-${i}`} />)}
  </div>
);

const EXPLORE_SS_KEY = "home.explore.state";

const useColumns = () => {
  const get = () => {
    if (typeof window === "undefined") return 2;
    if (window.matchMedia("(min-width: 1024px)").matches) return 5;
    if (window.matchMedia("(min-width: 768px)").matches) return 3;
    return 2;
  };
  const [cols, setCols] = useState(get);
  useEffect(() => {
    const onResize = () => setCols(get());
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return cols;
};

const ExploreArchive = () => {
  const [data, setData] = useState(null);
  // Restore tab + expansion from sessionStorage so back-navigation lands
  // exactly where the visitor left off (same subject still expanded).
  const initial = (() => {
    try { return JSON.parse(sessionStorage.getItem(EXPLORE_SS_KEY)) || {}; }
    catch { return {}; }
  })();
  const [tab, setTab] = useState(initial.tab || "subjects");
  const [openSubject, setOpenSubject] = useState(initial.openSubject || null);
  const [openRegion, setOpenRegion] = useState(initial.openRegion || null);
  const cols = useColumns();

  useEffect(() => {
    try {
      sessionStorage.setItem(EXPLORE_SS_KEY,
        JSON.stringify({ tab, openSubject, openRegion }));
    } catch { /* ignore */ }
  }, [tab, openSubject, openRegion]);

  useEffect(() => {
    let dead = false;
    axios.get(`${API}/home/explore`, { timeout: 15000 })
      .then((r) => { if (!dead) setData(r.data); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  if (!data) return null;
  const subjects = data.subjects || [];
  const regions = data.regions || [];

  // Chunk subjects into visual rows so the expansion renders directly
  // under the row containing the selected subject (not below the grid).
  const rows = [];
  for (let i = 0; i < subjects.length; i += cols) rows.push(subjects.slice(i, i + cols));

  return (
    <section data-testid="home-explore" className="border-t" style={{ borderColor: "#2D2A26", background: "#0B0A0D" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-20">
        <SectionHeader
          file="File 007 · Discovery Paths"
          title="Explore the Archive"
          subtitle="Every investigation, findable by subject or by geography. The Archive covers the record globally."
        />

        <div className="flex border w-fit mb-6 md:mb-8" style={{ borderColor: "#2D2A26", background: "#0F0E12" }}>
          {[["subjects", "Subjects", Layers], ["regions", "Regions", Globe2]].map(([key, label, Icon]) => {
            const on = tab === key;
            return (
              <button
                key={key}
                data-testid={`explore-tab-${key}`}
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 px-5 md:px-8 py-3 font-mono text-[10px] uppercase tracking-[0.3em] transition-colors ${
                  on ? "text-[#0B0A0D]" : "text-[#8A8377] hover:text-[#E4DFD3]"
                }`}
                style={{ background: on ? "#D92D20" : "transparent" }}
              >
                <Icon size={12} /> {label}
              </button>
            );
          })}
        </div>

        {tab === "subjects" && (
          <div key="subjects" className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 space-y-3 md:space-y-4">
            {rows.map((row, ri) => {
              const activeInRow = row.find((s) => s.slug === openSubject);
              return (
                <React.Fragment key={ri}>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                    {row.map((s) => (
                      <SubjectCard
                        key={s.slug}
                        s={s}
                        open={openSubject === s.slug}
                        onToggle={() => setOpenSubject(openSubject === s.slug ? null : s.slug)}
                      />
                    ))}
                  </div>
                  {activeInRow && (
                    <Expansion
                      items={activeInRow.dossiers}
                      testid={`subject-expansion-${activeInRow.slug}`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {tab === "regions" && (
          <div key="regions" className="border motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300" style={{ borderColor: "#2D2A26", background: "#0F0E12" }}>
            {regions.map((r) => (
              <React.Fragment key={r.slug}>
                <RegionRow
                  r={r}
                  open={openRegion === r.slug}
                  onToggle={() => setOpenRegion(openRegion === r.slug ? null : r.slug)}
                />
                {openRegion === r.slug && (
                  <div className="px-3 pb-3">
                    <Expansion items={r.dossiers} testid={`region-expansion-${r.slug}`} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ExploreArchive;
